(() => {
  if (window.__scrollRevealTabsInitialized) return;
  window.__scrollRevealTabsInitialized = true;

  const SELECTOR = "[data-scroll-reveal-tabs]";
  const sectionMotionState = new WeakMap();
  const motionModuleByUrl = new Map();
  const getMotionModulePromise = (section) => {
    const motionAssetUrl = section?.dataset.motionAssetUrl || "/assets/vendor.min.js";
    if (motionModuleByUrl.has(motionAssetUrl)) {
      return motionModuleByUrl.get(motionAssetUrl);
    }
    let modulePromise;
    try {
      modulePromise = import(motionAssetUrl);
    } catch (error) {
      modulePromise = Promise.resolve(null);
    }
    motionModuleByUrl.set(motionAssetUrl, modulePromise);
    return modulePromise;
  };

  const stopMotion = (element) => {
    if (!element) return;
    const activeAnimation = element.__srtMotionAnimation;
    if (activeAnimation && typeof activeAnimation.stop === "function") {
      activeAnimation.stop();
    }
    element.__srtMotionAnimation = null;
  };

  const playMotion = (element, animate2, keyframes, options) => {
    if (!element) return null;
    stopMotion(element);
    const toMilliseconds = (durationLike) => {
      if (typeof durationLike !== "number" || Number.isNaN(durationLike)) return 0;
      return durationLike <= 10 ? durationLike * 1000 : durationLike;
    };
    const toEasing = (easingLike) => {
      if (Array.isArray(easingLike) && easingLike.length === 4) {
        return `cubic-bezier(${easingLike.join(",")})`;
      }
      return typeof easingLike === "string" ? easingLike : "ease";
    };

    let animation = null;
    if (typeof animate2 === "function") {
      animation = animate2(element, keyframes, options);
    } else if (typeof element.animate === "function") {
      animation = element.animate(keyframes, {
        duration: toMilliseconds(options?.duration),
        delay: toMilliseconds(options?.delay),
        easing: toEasing(options?.easing),
        fill: "both",
      });
    } else {
      return null;
    }

    element.__srtMotionAnimation = animation;
    if (animation && animation.finished && typeof animation.finished.finally === "function") {
      animation.finished.finally(() => {
        if (element.__srtMotionAnimation === animation) {
          element.__srtMotionAnimation = null;
        }
      });
    }
    return animation;
  };

  const isReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const prepareScrollRevealTabControl = (control, isActive) => {
    if (!control) return;
    control.setAttribute("type", "button");
    control.setAttribute("tabindex", isActive ? "0" : "-1");
  };

  const syncScrollRevealTabStops = (root, activeIndex) => {
    if (!root) return;
    const revealed = root.classList.contains("is-revealed");
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const isVerticalScrollMode = (root.dataset.detailInteractionMode || "tabs") === "vertical_scroll";
    const tabsModeDesktop = !isMobile && !isVerticalScrollMode;

    const safeIndex = Math.max(0, activeIndex || 0);
    const introRegion = root.querySelector("[data-srt-intro-region]");
    if (introRegion) {
      introRegion.setAttribute("tabindex", revealed ? "-1" : "0");
    }
    const tabButtons = root.querySelectorAll("[data-tab-btn]");
    tabButtons.forEach((button, i) => {
      if (!tabsModeDesktop) {
        button.setAttribute("tabindex", "-1");
        return;
      }
      if (revealed) {
        button.setAttribute("type", "button");
        button.setAttribute("tabindex", "0");
        return;
      }
      prepareScrollRevealTabControl(button, i === safeIndex);
    });
    const detailDots = root.querySelectorAll("[data-detail-dot]");
    detailDots.forEach((dot) => {
      const enableDot = revealed && !isMobile && isVerticalScrollMode;
      if (!enableDot) {
        dot.removeAttribute("disabled");
        dot.setAttribute("aria-disabled", "true");
        dot.setAttribute("tabindex", "-1");
        return;
      }
      dot.removeAttribute("disabled");
      dot.setAttribute("type", "button");
      dot.setAttribute("aria-disabled", "false");
      dot.setAttribute("tabindex", "0");
    });
  };

  const activateTab = (root, index, options = {}) => {
    const { immediate = false } = options;
    const motionState = sectionMotionState.get(root) || {};
    const animate2 = motionState.animate2;
    const buttons = root.querySelectorAll("[data-tab-btn]");
    const medias = root.querySelectorAll("[data-tab-media]");
    const texts = root.querySelectorAll("[data-tab-text]");
    const sizers = root.querySelectorAll("[data-tab-sizer]");
    const detailDots = root.querySelectorAll("[data-detail-dot]");
    const safeIndex = Math.max(0, Math.min(Math.max(medias.length, texts.length, 1) - 1, index));
    const previousMedia = Array.from(medias).find((item) => item.classList.contains("is-active"));
    const nextMedia = medias[safeIndex];
    const previousText = Array.from(texts).find((item) => item.classList.contains("is-active"));
    const nextText = texts[safeIndex];

    buttons.forEach((button, i) => {
      const isActive = i === safeIndex;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive && animate2 && !immediate && !isReducedMotion()) {
        playMotion(
          button,
          animate2,
          { transform: ["scale(0.96)", "scale(1)"] },
          { duration: 0.22, easing: "ease-out" },
        );
      }
    });

    if (animate2 && !immediate && !isReducedMotion() && previousMedia && nextMedia && previousMedia !== nextMedia) {
      nextMedia.classList.add("is-active");
      nextMedia.style.pointerEvents = "auto";
      playMotion(
        nextMedia,
        animate2,
        { opacity: [0, 1], transform: ["translateY(0)", "translateY(0)"] },
        { duration: 0.24, easing: "ease-out" },
      );
      const hidePrevAnimation = playMotion(
        previousMedia,
        animate2,
        { opacity: [1, 0], transform: ["translateY(0)", "translateY(0)"] },
        { duration: 0.2, easing: "ease-in" },
      );
      if (hidePrevAnimation && hidePrevAnimation.finished) {
        hidePrevAnimation.finished.finally(() => {
          if (previousMedia !== nextMedia) {
            previousMedia.classList.remove("is-active");
            previousMedia.style.pointerEvents = "none";
          }
        });
      }
      medias.forEach((item) => {
        if (item !== previousMedia && item !== nextMedia) {
          item.classList.remove("is-active");
          item.style.pointerEvents = "none";
        }
      });
    } else {
      medias.forEach((item, i) => {
        item.classList.toggle("is-active", i === safeIndex);
        item.style.pointerEvents = i === safeIndex ? "auto" : "none";
      });
    }

    sizers.forEach((item, i) => {
      item.classList.toggle("is-active", i === safeIndex);
    });

    texts.forEach((item, i) => {
      item.classList.toggle("is-active", i === safeIndex);
    });

    if (animate2 && !immediate && !isReducedMotion() && nextText && previousText !== nextText) {
      playMotion(
        nextText,
        animate2,
        { opacity: [0, 1], transform: ["translateY(0)", "translateY(0)"] },
        { duration: 0.22, easing: "ease-out" },
      );
    }

    detailDots.forEach((dot, i) => {
      const isActive = i === safeIndex;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
      if (isActive && animate2 && !immediate && !isReducedMotion()) {
        playMotion(dot, animate2, { transform: ["scale(0.9)", "scale(1)"] }, { duration: 0.2, easing: "ease-out" });
      }
    });

    syncScrollRevealTabStops(root, safeIndex);
  };

  const createRevealController = ({
    section,
    sectionMotionStateRef,
    isReducedMotionFn,
    isMobileViewportFn,
    introPanel,
    detailPanel,
    mobilePanel,
    overlayLayer,
    backgroundImages,
    getFullSlideOffset,
    getIntroLayers,
    getRevealLayers,
    getCurrentTranslateYPx,
    playMotionFn,
    onRevealStateChange,
  }) => {
    let revealState = section.classList.contains("is-revealed");
    let verticalDetailTransitionStartedAt = 0;
    let verticalDetailTransitionDurationMs = 0;
    let verticalDetailTransitionUntil = 0;
    const pickRevealEnterStartY = (currentY, fallbackY) => {
      if (!Number.isFinite(currentY)) return fallbackY;
      return Math.abs(currentY) < 1 ? fallbackY : currentY;
    };
    const pickIntroReturnStartY = (currentY, fallbackOffset) => {
      if (!Number.isFinite(currentY)) return -fallbackOffset;
      return Math.abs(currentY) < 1 ? -fallbackOffset : currentY;
    };

    const setRevealState = (revealed, immediate = false) => {
      const motionState = sectionMotionStateRef.get(section) || {};
      const animate2 = motionState.animate2;
      const supportsNativeMotion = typeof document.documentElement?.animate === "function";
      const blurStrength = getComputedStyle(section).getPropertyValue("--srt-blur-strength").trim() || "0px";
      const overlayOpacity = getComputedStyle(section).getPropertyValue("--srt-overlay-opacity").trim() || "0";
      const canAnimate = !immediate && (typeof animate2 === "function" || supportsNativeMotion);
      const fullSlideOffset = getFullSlideOffset();
      revealState = revealed;
      section.classList.toggle("is-revealed", revealed);

      const commitDetailA11yStyles = () => {
        if (!detailPanel) return;
        if (revealed) {
          detailPanel.style.visibility = "visible";
          detailPanel.style.removeProperty("pointer-events");
        }
        if (typeof onRevealStateChange === "function") {
          onRevealStateChange(revealed);
        }
      };

      if (!introPanel || !detailPanel) {
        commitDetailA11yStyles();
        return;
      }

      if (!canAnimate) {
        const isMobileNow = isMobileViewportFn();
        if (mobilePanel) {
          mobilePanel.style.removeProperty("display");
          mobilePanel.style.removeProperty("flex-direction");
          mobilePanel.style.removeProperty("align-items");
        }
        introPanel.style.opacity = "1";
        introPanel.style.transform = "translateY(0)";
        introPanel.style.visibility = revealed ? "hidden" : "visible";
        introPanel.style.pointerEvents = revealed ? "none" : "auto";
        getIntroLayers().forEach(({ element, offset }) => {
          if (!element) return;
          element.style.transform = revealed ? `translateY(-${offset}px)` : "translateY(0)";
        });

        detailPanel.style.opacity = "1";
        detailPanel.style.transform = "translateY(0)";
        detailPanel.style.visibility = revealed ? "visible" : "hidden";
        if (revealed) {
          detailPanel.style.pointerEvents = "auto";
        } else {
          detailPanel.style.removeProperty("pointer-events");
        }
        if (!isMobileNow) {
          getRevealLayers().forEach(({ element, offset }) => {
            if (!element) return;
            element.style.transform = revealed ? "translateY(0)" : `translateY(${offset}px)`;
          });
        } else {
          getRevealLayers().forEach(({ element }) => {
            if (!element) return;
            element.style.removeProperty("transform");
          });
        }

        if (overlayLayer) {
          overlayLayer.style.opacity = revealed ? overlayOpacity : "0";
          overlayLayer.style.backdropFilter = revealed ? `blur(${blurStrength})` : "blur(0px)";
        }
        backgroundImages.forEach((image) => {
          image.style.filter = revealed ? `blur(${blurStrength})` : "blur(0px)";
        });
        commitDetailA11yStyles();
        return;
      }

      if (revealed) {
        const isMobileNow = isMobileViewportFn();
        const layerMaxDuration = isMobileNow
          ? 0.8
          : getRevealLayers(false).reduce(
              (maxDuration, layer) => Math.max(maxDuration, Number(layer.duration) || 0),
              0,
            );
        const revealTransitionDuration = Math.max(layerMaxDuration, 1.6);
        verticalDetailTransitionDurationMs = Math.round(revealTransitionDuration * 1000);
        verticalDetailTransitionStartedAt = Date.now();
        verticalDetailTransitionUntil = verticalDetailTransitionStartedAt + verticalDetailTransitionDurationMs;
        if (mobilePanel) {
          mobilePanel.style.removeProperty("display");
          mobilePanel.style.removeProperty("flex-direction");
          mobilePanel.style.removeProperty("align-items");
        }
        detailPanel.style.visibility = "visible";
        detailPanel.style.pointerEvents = "auto";
        if (isMobileNow) {
          const startY = pickRevealEnterStartY(getCurrentTranslateYPx(detailPanel), fullSlideOffset);
          playMotionFn(
            detailPanel,
            animate2,
            { transform: [`translateY(${startY}px)`, "translateY(0)"] },
            { duration: 0.8, easing: [0.22, 1, 0.36, 1] },
          );
        } else {
          getRevealLayers(false).forEach(({ element, offset, duration }) => {
            if (!element) return;
            const startY = pickRevealEnterStartY(getCurrentTranslateYPx(element), offset);
            playMotionFn(
              element,
              animate2,
              { transform: [`translateY(${startY}px)`, "translateY(0)"] },
              { duration, easing: [0.22, 1, 0.36, 1] },
            );
          });
        }

        introPanel.style.visibility = "visible";
        introPanel.style.pointerEvents = "none";
        let introOut = null;
        let introOutMaxDuration = -1;
        getIntroLayers(true).forEach(({ element, offset, duration }) => {
          if (!element) return;
          const startY = getCurrentTranslateYPx(element);
          const animation = playMotionFn(
            element,
            animate2,
            { transform: [`translateY(${startY}px)`, `translateY(-${offset}px)`] },
            { duration, easing: [0.36, 1, 0.36, 1] },
          );
          if (duration > introOutMaxDuration) {
            introOutMaxDuration = duration;
            introOut = animation;
          }
        });
        if (introOut && introOut.finished) {
          introOut.finished.finally(() => {
            if (revealState) introPanel.style.visibility = "hidden";
          });
        }
      } else {
        verticalDetailTransitionDurationMs = 0;
        verticalDetailTransitionStartedAt = 0;
        verticalDetailTransitionUntil = 0;
        const isMobileNow = isMobileViewportFn();
        if (mobilePanel) {
          mobilePanel.style.removeProperty("display");
          mobilePanel.style.removeProperty("flex-direction");
          mobilePanel.style.removeProperty("align-items");
        }
        introPanel.style.visibility = "visible";
        introPanel.style.pointerEvents = "auto";
        getIntroLayers(false).forEach(({ element, duration, offset }) => {
          if (!element) return;
          const startY = pickIntroReturnStartY(getCurrentTranslateYPx(element), offset);
          playMotionFn(
            element,
            animate2,
            { transform: [`translateY(${startY}px)`, "translateY(0)"] },
            { duration, easing: [0.22, 1, 0.36, 1] },
          );
        });

        detailPanel.style.visibility = "visible";
        detailPanel.style.removeProperty("pointer-events");
        let detailOut = null;
        let maxDuration = -1;
        if (isMobileNow) {
          const startY = getCurrentTranslateYPx(detailPanel);
          detailOut = playMotionFn(
            detailPanel,
            animate2,
            { transform: [`translateY(${startY}px)`, `translateY(${fullSlideOffset}px)`] },
            { duration: 0.8, easing: [0.22, 1, 0.36, 1] },
          );
        } else {
          getRevealLayers(true).forEach(({ element, offset, duration }) => {
            if (!element) return;
            const startY = getCurrentTranslateYPx(element);
            const animation = playMotionFn(
              element,
              animate2,
              { transform: [`translateY(${startY}px)`, `translateY(${offset}px)`] },
              { duration, easing: [0.22, 1, 0.36, 1] },
            );
            if (duration > maxDuration) {
              maxDuration = duration;
              detailOut = animation;
            }
          });
        }
        if (detailOut && detailOut.finished) {
          detailOut.finished.finally(() => {
            if (!revealState) {
              detailPanel.style.visibility = "hidden";
              if (mobilePanel) {
                mobilePanel.style.removeProperty("display");
              }
            }
          });
        } else if (!revealState) {
          detailPanel.style.visibility = "hidden";
          introPanel.style.visibility = "visible";
          introPanel.style.pointerEvents = "auto";
          if (mobilePanel) {
            mobilePanel.style.removeProperty("display");
          }
        }
      }

      if (overlayLayer) {
        playMotionFn(
          overlayLayer,
          animate2,
          {
            opacity: [revealed ? "0" : overlayOpacity, revealed ? overlayOpacity : "0"],
            backdropFilter: [
              revealed ? "blur(0px)" : `blur(${blurStrength})`,
              revealed ? `blur(${blurStrength})` : "blur(0px)",
            ],
          },
          { duration: 0.8, easing: [0.22, 1, 0.36, 1] },
        );
      }

      backgroundImages.forEach((image) => {
        playMotionFn(
          image,
          animate2,
          {
            filter: [
              revealed ? "blur(0px)" : `blur(${blurStrength})`,
              revealed ? `blur(${blurStrength})` : "blur(0px)",
            ],
          },
          { duration: 0.4, easing: "ease" },
        );
      });
      commitDetailA11yStyles();
    };

    const syncRevealFromScroll = ({
      passed,
      rect,
      viewportHeight,
      hasInitialRevealPass,
      mobileBottomInView = true,
    }) => {
      const isRevealed = revealState;
      const revealEnterPx = viewportHeight * 0.2;
      const revealExitPx = viewportHeight * 0.2;
      let shouldReveal = isRevealed ? passed >= revealExitPx : passed >= revealEnterPx;
      // 移动端：仅当内容未超出视口下沿时，才允许进入下一屏（不判断上方）
      // sticky 元素 getBoundingClientRect 恒定在顶部，不能用 rect 判断，改用 passed >= overflow
      if (!isRevealed && shouldReveal && !mobileBottomInView) {
        const overflowPx =
          Number.parseFloat(section.style.getPropertyValue("--srt-sticky-extra-height")) || 0;
        if (passed < Math.max(0, overflowPx - 2)) {
          shouldReveal = false;
        }
      }
      if (shouldReveal !== revealState) {
        const isNearViewport = rect.bottom > -viewportHeight * 0.5 && rect.top < viewportHeight * 1.5;
        const forceImmediateSync = !hasInitialRevealPass || !isNearViewport;
        setRevealState(shouldReveal, forceImmediateSync);
      }
      return { shouldReveal, revealEnterPx };
    };

    return {
      setRevealState,
      isRevealed: () => revealState,
      getVerticalTransitionMeta: () => ({
        startedAt: verticalDetailTransitionStartedAt,
        durationMs: verticalDetailTransitionDurationMs,
        until: verticalDetailTransitionUntil,
      }),
      syncRevealFromScroll,
    };
  };

  const createVerticalPager = ({
    isVerticalScrollMode,
    totalDesktopSlides,
    detailScrollScreens,
    isMobileViewportFn,
    isPadViewportFn,
    activateTabFn,
    section,
    getActiveDesktopSlide,
    setActiveDesktopSlide,
    getVerticalTransitionMeta,
  }) => {
    let previousPassed = 0;

    const updateFromScroll = ({ shouldReveal, passed, stickyHeight, revealEnterPx }) => {
      if (!(isVerticalScrollMode && shouldReveal && totalDesktopSlides > 0)) {
        previousPassed = passed;
        return;
      }

      if (isMobileViewportFn() || isPadViewportFn()) {
        previousPassed = passed;
        return;
      }

      const scrollDeltaForVerticalPaging = passed - previousPassed;
      const movingDownForVerticalPaging = scrollDeltaForVerticalPaging > 0.1;
      const movingUpForVerticalPaging = scrollDeltaForVerticalPaging < -0.1;
      const detailStartPx = revealEnterPx;
      const detailPassed = Math.max(0, passed - detailStartPx);
      const detailTotalPx = Math.max(stickyHeight * detailScrollScreens, 1);
      const pageScrollPx = Math.max(detailTotalPx / Math.max(totalDesktopSlides, 1), 1);
      let rawIndex = Math.floor(detailPassed / pageScrollPx);
      if (movingDownForVerticalPaging) {
        rawIndex = Math.ceil(detailPassed / pageScrollPx);
      } else if (movingUpForVerticalPaging) {
        rawIndex = Math.floor(detailPassed / pageScrollPx);
      }

      let nextSlide = Math.max(0, Math.min(totalDesktopSlides - 1, rawIndex));
      const transitionMeta = getVerticalTransitionMeta();
      const nowMs = Date.now();
      if (nowMs < transitionMeta.until && transitionMeta.durationMs > 0) {
        const transitionProgress = Math.min(
          Math.max((nowMs - transitionMeta.startedAt) / transitionMeta.durationMs, 0),
          1,
        );
        const maxSlideAllowed = Math.max(
          0,
          Math.min(totalDesktopSlides - 1, Math.floor(transitionProgress * totalDesktopSlides)),
        );
        nextSlide = Math.min(nextSlide, maxSlideAllowed);
      }

      if (nextSlide !== getActiveDesktopSlide()) {
        activateTabFn(section, nextSlide);
        setActiveDesktopSlide(nextSlide);
      }

      previousPassed = passed;
    };

    return { updateFromScroll };
  };

  const createResponsiveReset = ({
    isMobileViewportFn,
    isPadViewportFn,
    mobilePanel,
    introPanel,
    detailPanel,
    overlayLayer,
    backgroundImages,
    getIntroLayers,
    getRevealLayers,
    onCrossBreakpoint,
  }) => {
    let previousIsMobileViewport = isMobileViewportFn();
    let previousIsPadViewport = isPadViewportFn();

    const resetResponsiveInlineStyles = () => {
      if (mobilePanel) {
        mobilePanel.style.removeProperty("display");
        mobilePanel.style.removeProperty("transform");
        mobilePanel.style.removeProperty("visibility");
        mobilePanel.style.removeProperty("pointer-events");
      }
      introPanel.style.removeProperty("transform");
      introPanel.style.removeProperty("visibility");
      introPanel.style.removeProperty("pointer-events");
      introPanel.style.removeProperty("opacity");
      getIntroLayers().forEach(({ element }) => {
        if (!element) return;
        element.style.removeProperty("transform");
      });
      detailPanel.style.removeProperty("transform");
      detailPanel.style.removeProperty("visibility");
      detailPanel.style.removeProperty("pointer-events");
      detailPanel.style.removeProperty("opacity");
      if (overlayLayer) {
        overlayLayer.style.removeProperty("opacity");
        overlayLayer.style.removeProperty("backdrop-filter");
      }
      backgroundImages.forEach((image) => {
        image.style.removeProperty("filter");
      });
      getRevealLayers().forEach(({ element }) => {
        if (!element) return;
        element.style.removeProperty("transform");
      });
    };

    const handleResize = () => {
      const currentIsMobileViewport = isMobileViewportFn();
      const currentIsPadViewport = isPadViewportFn();
      const crossedBreakpoint =
        currentIsMobileViewport !== previousIsMobileViewport || currentIsPadViewport !== previousIsPadViewport;
      if (!crossedBreakpoint) return;

      resetResponsiveInlineStyles();
      if (typeof onCrossBreakpoint === "function") {
        onCrossBreakpoint();
      }
      previousIsMobileViewport = currentIsMobileViewport;
      previousIsPadViewport = currentIsPadViewport;
    };

    return {
      resetResponsiveInlineStyles,
      handleResize,
    };
  };

  const setupSection = (section) => {
    if (section.dataset.bound === "true") return;
    section.dataset.bound = "true";

    getMotionModulePromise(section)
      .then((motion) => {
        if (!motion || section.dataset.motionInviewBound === "true") return;
        const { animate: animate2, inView, stagger: stagger3 } = motion;
        if (typeof animate2 !== "function" || typeof inView !== "function") return;
        sectionMotionState.set(section, { animate2, inView, stagger3 });
        if (isReducedMotion()) return;

        const revealTargets = section.querySelectorAll(
          ".scroll-reveal-tabs__eyebrow, .scroll-reveal-tabs__headline, .scroll-reveal-tabs__desc, .scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__media, .scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__right",
        );
        if (!revealTargets.length) return;

        section.dataset.motionInviewBound = "true";
        let hasAnimated = false;
        inView(
          section,
          () => {
            if (hasAnimated) return undefined;
            hasAnimated = true;
            animate2(
              revealTargets,
              { opacity: [0, 1], transform: ["translateY(16px)", "translateY(0)"] },
              {
                duration: 0.45,
                easing: "ease",
                delay: typeof stagger3 === "function" ? stagger3(0.06, { easing: "ease-out" }) : 0,
              },
            );
            return undefined;
          },
          { margin: "-12% 0px -8% 0px" },
        );
      })
      .catch(() => {
        /* Keep section behavior unchanged if animation module fails to load. */
      });

    const scrollScreens = Number(section.dataset.scrollScreens || 2);
    const detailScrollScreens = Number(section.dataset.detailScrollScreens || Math.max(scrollScreens - 1, 0));
    const introScrollScreens = Number(section.dataset.introScrollScreens || 1);
    const detailInteractionMode = section.dataset.detailInteractionMode || "tabs";
    const isVerticalScrollMode = detailInteractionMode === "vertical_scroll";
    const isMobileViewport = () => window.matchMedia("(max-width: 767px)").matches;
    const isPadViewport = () => window.matchMedia("(max-width: 1279px) and (min-width: 768px)").matches;
    const mobileDetail = section.querySelector("[data-mobile-carousel]");
    const mobileTextBlocks = section.querySelectorAll(
      ".scroll-reveal-tabs__mobile .scroll-reveal-tabs__tab-title, .scroll-reveal-tabs__mobile .scroll-reveal-tabs__tab-desc",
    );
    let mobileDetailHeight = 748;
    const getBasePaddingPx = (element, side) => {
      const key = `srtBasePadding${side}`;
      if (!element.dataset[key]) {
        const computed = getComputedStyle(element)[`padding${side}`];
        element.dataset[key] = String(Number.parseFloat(computed) || 0);
      }
      return Number.parseFloat(element.dataset[key]) || 0;
    };
    const updateMobileTextSpacing = (scale) => {
      mobileTextBlocks.forEach((element) => {
        if (scale >= 0.9999) {
          element.style.removeProperty("padding-top");
          element.style.removeProperty("padding-right");
          element.style.removeProperty("padding-bottom");
          element.style.removeProperty("padding-left");
          return;
        }
        const top = getBasePaddingPx(element, "Top");
        const right = getBasePaddingPx(element, "Right");
        const bottom = getBasePaddingPx(element, "Bottom");
        const left = getBasePaddingPx(element, "Left");
        element.style.paddingTop = `${(top * scale).toFixed(3)}px`;
        element.style.paddingRight = `${(right * scale).toFixed(3)}px`;
        element.style.paddingBottom = `${(bottom * scale).toFixed(3)}px`;
        element.style.paddingLeft = `${(left * scale).toFixed(3)}px`;
      });
    };
    const getBaseScreenHeight = () => {
      const styles = getComputedStyle(section);
      const minHeight = Number.parseFloat(styles.getPropertyValue("--srt-screen-min-height")) || 400;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || minHeight;
      return Math.max(viewportHeight, minHeight);
    };
    let maxNavChromeHeight = Number.parseInt(section.dataset.srtMaxNavChrome || "0", 10) || 0;
    const syncNavChromeHeight = () => {
      const headerContainerEl = document.querySelector("[data-header-container]");
      if (headerContainerEl) {
        maxNavChromeHeight = Math.max(
          maxNavChromeHeight,
          Math.round(headerContainerEl.getBoundingClientRect().height || headerContainerEl.offsetHeight || 0),
        );
      }
      section.dataset.srtMaxNavChrome = String(maxNavChromeHeight);
      section.style.setProperty("--srt-nav-chrome-height", `${maxNavChromeHeight}px`);
      section.style.setProperty("--sticky-area-height", `${maxNavChromeHeight}px`);
    };
    const isMobileLandscape = () =>
      isMobileViewport() &&
      (window.innerWidth || document.documentElement.clientWidth || 0) >
        (window.innerHeight || document.documentElement.clientHeight || 0);
    const syncMobileWideClass = () => {
      section.classList.toggle("is-mobile-wide", isMobileLandscape());
    };
    const syncMobileMediaMaxHeight = () => {
      if (!isMobileViewport()) return;
      // 仅横屏（宽 > 高）限高，竖屏恢复原始效果
      if (!isMobileLandscape()) {
        section.style.removeProperty("--srt-mobile-media-max-height");
        return;
      }

      const navChrome = Number.parseFloat(getComputedStyle(section).getPropertyValue("--srt-nav-chrome-height")) || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 748;
      const visibleHeight = Math.max(viewportHeight - navChrome, 1);
      const maxMediaHeight = Math.max(120, Math.floor(visibleHeight * 0.9));

      section.style.setProperty("--srt-mobile-media-max-height", `${maxMediaHeight}px`);
    };
    const syncMobileSectionHeightFromDom = () => {
      const stickyEl = section.querySelector(".scroll-reveal-tabs__sticky");
      const containerEl = section.querySelector(".scroll-reveal-tabs__container");
      // 仅横屏（宽 > 高）做层高同步，竖屏恢复原始效果（避免 inline 高度与原始 absolute 布局互搏）
      if (!isMobileLandscape() || !stickyEl || !containerEl) {
        section.style.removeProperty("--srt-screen-height");
        section.style.removeProperty("--srt-sticky-layer-height");
        section.style.removeProperty("--srt-mobile-media-max-height");
        section.style.setProperty("--srt-sticky-extra-height", "0px");
        return;
      }

      const navChrome = Number.parseFloat(getComputedStyle(section).getPropertyValue("--srt-nav-chrome-height")) || 0;
      const styles = getComputedStyle(section);
      const minLayerHeight =
        Number.parseFloat(styles.getPropertyValue("--srt-mobile-sticky-min-height")) ||
        Math.max(getBaseScreenHeight() - navChrome, 1);
      const containerHeight = Math.ceil(containerEl.getBoundingClientRect().height || containerEl.offsetHeight || 0);
      const layerHeight = Math.max(minLayerHeight, containerHeight);

      section.style.setProperty("--srt-sticky-layer-height", `${layerHeight}px`);

      const baseScreenHeight = getBaseScreenHeight();
      const stickyHeight = Math.max(
        layerHeight,
        Math.ceil(stickyEl.getBoundingClientRect().height || stickyEl.offsetHeight || 0),
      );
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || baseScreenHeight;
      const visibleLayerViewport = Math.max(viewportHeight - navChrome, 1);
      const contentOverflowPx = Math.max(0, Math.ceil(layerHeight - visibleLayerViewport));

      section.style.setProperty("--srt-screen-height", `${Math.max(baseScreenHeight, stickyHeight + navChrome)}px`);
      section.style.setProperty("--srt-sticky-extra-height", `${contentOverflowPx}px`);

      if (typeof section.__srtRecalcRevealMetrics === "function") {
        section.__srtRecalcRevealMetrics();
      }
    };
    let mobileLayoutObserver = null;
    const bindMobileLayoutObserver = () => {
      if (!isMobileViewport() || typeof ResizeObserver === "undefined") return;

      const stickyEl = section.querySelector(".scroll-reveal-tabs__sticky");
      const containerEl = section.querySelector(".scroll-reveal-tabs__container");
      const observeTargets = [stickyEl, containerEl, introPanel, mobileDetail, detailPanel].filter(Boolean);
      if (!observeTargets.length) return;

      if (!mobileLayoutObserver) {
        mobileLayoutObserver = new ResizeObserver(() => {
          syncMobileSectionHeightFromDom();
        });
      }

      observeTargets.forEach((target) => {
        if (target && !target.dataset.srtMobileLayoutObserved) {
          mobileLayoutObserver.observe(target);
          target.dataset.srtMobileLayoutObserved = "true";
        }
      });
    };
    const applyPortraitMobileDetailScale = () => {
      section.style.removeProperty("--srt-screen-height");
      section.style.removeProperty("--srt-sticky-layer-height");
      section.style.removeProperty("--srt-mobile-media-max-height");
      section.style.setProperty("--srt-sticky-extra-height", "0px");

      if (mobileDetail) {
        const measuredMobileDetailHeight = mobileDetail.offsetHeight || mobileDetail.scrollHeight || 0;
        if (measuredMobileDetailHeight > 0) {
          mobileDetailHeight = measuredMobileDetailHeight;
        }
      }
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 748;
      const stickyAreaRaw = getComputedStyle(section).getPropertyValue("--sticky-area-height");
      const stickyAreaHeight = Math.max(Number.parseFloat(stickyAreaRaw) || 0, 0);
      const oneScreenHeight = Math.max(viewportHeight - stickyAreaHeight, 1);
      const viewportScale = oneScreenHeight >= 748 ? 1 : Math.max(oneScreenHeight / 748, 0.01);
      const minDetailScale = mobileDetailHeight > 0 ? Math.min(1, 400 / mobileDetailHeight) : 0;
      const scale = Math.min(1, Math.max(viewportScale, minDetailScale));
      section.style.setProperty("--srt-mobile-detail-scale", scale.toFixed(4));
      updateMobileTextSpacing(scale);
    };
    const runMobileLayoutSync = () => {
      syncNavChromeHeight();
      syncMobileWideClass();

      if (!isMobileViewport()) {
        section.style.setProperty("--srt-mobile-detail-scale", "1");
        section.style.setProperty("--srt-sticky-extra-height", "0px");
        section.style.removeProperty("--srt-screen-height");
        section.style.removeProperty("--srt-sticky-layer-height");
        section.style.removeProperty("--srt-mobile-media-max-height");
        updateMobileTextSpacing(1);
        return;
      }

      if (isMobileLandscape()) {
        section.style.setProperty("--srt-mobile-detail-scale", "1");
        updateMobileTextSpacing(1);
        syncMobileMediaMaxHeight();
        bindMobileLayoutObserver();
        syncMobileSectionHeightFromDom();
        [
          section.querySelector(".scroll-reveal-tabs__intro"),
          section.querySelector(".scroll-reveal-tabs__detail"),
          section.querySelector(".scroll-reveal-tabs__mobile"),
        ]
          .filter(Boolean)
          .forEach((element) => {
            element.style.removeProperty("transform");
          });
        return;
      }

      applyPortraitMobileDetailScale();
    };
    const updateMobileDetailScale = () => {
      runMobileLayoutSync();
      if (!isMobileViewport()) return;

      window.requestAnimationFrame(() => {
        if (isMobileLandscape()) {
          syncMobileMediaMaxHeight();
          syncMobileSectionHeightFromDom();
          window.requestAnimationFrame(() => {
            syncMobileMediaMaxHeight();
            syncMobileSectionHeightFromDom();
          });
          return;
        }
        applyPortraitMobileDetailScale();
      });
    };
    const tabButtons = section.querySelectorAll("[data-tab-btn]");
    const detailDots = section.querySelectorAll("[data-detail-dot]");
    const totalDesktopSlides = section.querySelectorAll("[data-tab-media]").length;
    const desktopPanel = section.querySelector(".scroll-reveal-tabs__panel--desktop");
    const introPanel = section.querySelector(".scroll-reveal-tabs__intro");
    const detailPanel = section.querySelector(".scroll-reveal-tabs__detail");
    const mobilePanel = section.querySelector(".scroll-reveal-tabs__mobile");
    const overlayLayer = section.querySelector(".scroll-reveal-tabs__overlay");
    const backgroundImages = section.querySelectorAll(".scroll-reveal-tabs__bg-image");
    let activeDesktopSlide = Math.max(
      0,
      Array.from(section.querySelectorAll("[data-tab-media]")).findIndex((item) =>
        item.classList.contains("is-active"),
      ),
    );
    const detailMedia = section.querySelector(".scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__media");
    const detailDotsGroup = section.querySelector(
      ".scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__detail-dots",
    );
    const detailTabs = section.querySelector(".scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__tabs");
    const getActiveDetailTitle = () =>
      section.querySelector(
        ".scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__text-item.is-active .scroll-reveal-tabs__tab-title",
      );
    const getActiveDetailDesc = () =>
      section.querySelector(
        ".scroll-reveal-tabs__panel--desktop .scroll-reveal-tabs__text-item.is-active .scroll-reveal-tabs__tab-desc",
      );
    const getIntroLine1 = () => section.querySelector(".scroll-reveal-tabs__eyebrow");
    const getIntroLine2 = () => section.querySelector(".scroll-reveal-tabs__headline");
    const getIntroLine3 = () => section.querySelector(".scroll-reveal-tabs__desc");
    const getFullSlideOffset = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
      const stickyAreaRaw = getComputedStyle(section).getPropertyValue("--sticky-area-height");
      const stickyAreaHeight = Math.max(Number.parseFloat(stickyAreaRaw) || 0, 0);
      const oneScreenHeight = Math.max(viewportHeight - stickyAreaHeight, 1);
      const detailHeight = Math.max(detailPanel?.getBoundingClientRect().height || 0, 1);
      return Math.max(oneScreenHeight, detailHeight);
    };
    const getCurrentTranslateYPx = (element) => {
      if (!element) return 0;
      const transform = getComputedStyle(element).transform;
      if (!transform || transform === "none") return 0;
      const matrix3dMatch = transform.match(/^matrix3d\((.+)\)$/);
      if (matrix3dMatch) {
        const values = matrix3dMatch[1].split(",").map((value) => Number.parseFloat(value.trim()));
        return Number.isFinite(values[13]) ? values[13] : 0;
      }
      const matrix2dMatch = transform.match(/^matrix\((.+)\)$/);
      if (matrix2dMatch) {
        const values = matrix2dMatch[1].split(",").map((value) => Number.parseFloat(value.trim()));
        return Number.isFinite(values[5]) ? values[5] : 0;
      }
      return 0;
    };
    const getRevealLayers = (reverseDurations = false) => {
      const fullOffset = getFullSlideOffset();
      const layers = [
        { element: detailMedia, offset: fullOffset, duration: 1.4 }, // fastest
        { element: detailDotsGroup, offset: fullOffset, duration: 1.4 }, // move with image
        { element: detailTabs, offset: fullOffset, duration: 1.5 },
        { element: getActiveDetailTitle(), offset: fullOffset, duration: 1.6 },
        { element: getActiveDetailDesc(), offset: fullOffset, duration: 1.8 }, // slowest
      ];
      if (!reverseDurations) return layers;
      const reversedDurations = layers.map((layer) => layer.duration).reverse();
      return layers.map((layer, index) => ({ ...layer, duration: reversedDurations[index] }));
    };
    const getIntroLayers = (reverseDurations = false) => {
      const fullOffset = getFullSlideOffset();
      const layers = [
        { element: getIntroLine1(), offset: fullOffset, duration: 1.6 }, // line 1 slowest
        { element: getIntroLine2(), offset: fullOffset, duration: 1.5 },
        { element: getIntroLine3(), offset: fullOffset, duration: 1.4 }, // line 3 fastest
      ];
      if (!reverseDurations) return layers;
      const reversedDurations = layers.map((layer) => layer.duration).reverse();
      return layers.map((layer, index) => ({ ...layer, duration: reversedDurations[index] }));
    };

    const revealController = createRevealController({
      section,
      sectionMotionStateRef: sectionMotionState,
      isReducedMotionFn: isReducedMotion,
      isMobileViewportFn: isMobileViewport,
      introPanel,
      detailPanel,
      mobilePanel,
      overlayLayer,
      backgroundImages,
      getFullSlideOffset,
      getIntroLayers,
      getRevealLayers,
      getCurrentTranslateYPx,
      playMotionFn: playMotion,
      onRevealStateChange: () => {
        syncScrollRevealTabStops(section, activeDesktopSlide);
        window.requestAnimationFrame(() => {
          updateMobileDetailScale();
          window.requestAnimationFrame(() => {
            syncMobileSectionHeightFromDom();
          });
        });
      },
    });

    activateTab(section, activeDesktopSlide, { immediate: true });
    revealController.setRevealState(revealController.isRevealed(), true);
    syncScrollRevealTabStops(section, activeDesktopSlide);

    const responsiveReset = createResponsiveReset({
      isMobileViewportFn: isMobileViewport,
      isPadViewportFn: isPadViewport,
      mobilePanel,
      introPanel,
      detailPanel,
      overlayLayer,
      backgroundImages,
      getIntroLayers,
      getRevealLayers,
      onCrossBreakpoint: () => {
        activateTab(section, activeDesktopSlide, { immediate: true });
        revealController.setRevealState(revealController.isRevealed(), true);
        if (section.classList.contains("is-revealed") && detailPanel) {
          detailPanel.style.visibility = "visible";
          detailPanel.style.removeProperty("pointer-events");
        }
        syncScrollRevealTabStops(section, activeDesktopSlide);
      },
    });

    const ensureDetailVisibleForTab = (tabIndex) => {
      const safeIndex = Math.max(0, Math.min(totalDesktopSlides - 1, tabIndex));
      if (!revealController.isRevealed()) {
        revealController.setRevealState(true, true);
      }
      activateTab(section, safeIndex);
      activeDesktopSlide = safeIndex;
      syncScrollRevealTabStops(section, safeIndex);
    };

    tabButtons.forEach((button, buttonIndex) => {
      const activateFromButton = () => {
        ensureDetailVisibleForTab(Number(button.dataset.index || 0));
      };
      button.addEventListener("click", activateFromButton);
      button.addEventListener("focus", () => {
        ensureDetailVisibleForTab(Number(button.dataset.index || buttonIndex));
      });
      button.addEventListener("keydown", (event) => {
        if (event.key === "Tab" && !event.shiftKey && buttonIndex < tabButtons.length - 1) {
          const nextBtn = tabButtons[buttonIndex + 1];
          if (nextBtn && section.classList.contains("is-revealed")) {
            event.preventDefault();
            ensureDetailVisibleForTab(buttonIndex + 1);
            nextBtn.focus();
            return;
          }
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activateFromButton();
          return;
        }

        let focusIndex = buttonIndex;
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          event.preventDefault();
          focusIndex = Math.min(tabButtons.length - 1, buttonIndex + 1);
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          event.preventDefault();
          focusIndex = Math.max(0, buttonIndex - 1);
        } else if (event.key === "Home") {
          event.preventDefault();
          focusIndex = 0;
        } else if (event.key === "End") {
          event.preventDefault();
          focusIndex = tabButtons.length - 1;
        } else {
          return;
        }

        ensureDetailVisibleForTab(focusIndex);
        tabButtons[focusIndex]?.focus();
      });
    });

    if (isVerticalScrollMode) {
      detailDots.forEach((dot) => {
        const activateFromDot = () => {
          const nextIndex = Number(dot.dataset.index || 0);
          activateTab(section, nextIndex);
          activeDesktopSlide = nextIndex;
        };
        dot.addEventListener("click", activateFromDot);
        dot.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activateFromDot();
          }
        });
      });
    }

    if (isVerticalScrollMode && desktopPanel) {
      let padTouching = false;
      let padGestureHorizontal = false;
      let padStartX = 0;
      let padStartY = 0;
      let padLockedIndex = 0;
      const padSwipeThreshold = 28;

      const onPadTouchStart = (event) => {
        if (!isPadViewport() || isMobileViewport()) return;
        if (!event.touches || event.touches.length === 0) return;
        padTouching = true;
        padGestureHorizontal = false;
        padStartX = event.touches[0].clientX;
        padStartY = event.touches[0].clientY;
        padLockedIndex = activeDesktopSlide;
      };

      const onPadTouchMove = (event) => {
        if (!padTouching || !isPadViewport() || !event.touches || event.touches.length === 0) return;
        const deltaX = event.touches[0].clientX - padStartX;
        const deltaY = event.touches[0].clientY - padStartY;

        if (!padGestureHorizontal && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY) + 6) {
          padGestureHorizontal = true;
        }

        if (!padGestureHorizontal) return;
        event.preventDefault();

        if (Math.abs(deltaX) < padSwipeThreshold) {
          padLockedIndex = activeDesktopSlide;
          return;
        }

        const direction = deltaX < 0 ? 1 : -1;
        padLockedIndex = Math.max(0, Math.min(totalDesktopSlides - 1, activeDesktopSlide + direction));
      };

      const onPadTouchEnd = () => {
        if (!padTouching) return;
        padTouching = false;
        if (!padGestureHorizontal) return;
        if (padLockedIndex !== activeDesktopSlide) {
          activateTab(section, padLockedIndex);
          activeDesktopSlide = padLockedIndex;
        }
      };

      desktopPanel.addEventListener("touchstart", onPadTouchStart, { passive: true });
      desktopPanel.addEventListener("touchmove", onPadTouchMove, { passive: false });
      desktopPanel.addEventListener("touchend", onPadTouchEnd, { passive: true });
      desktopPanel.addEventListener("touchcancel", onPadTouchEnd, { passive: true });
    }

    const mobileTrack = section.querySelector("[data-mobile-carousel-track]");
    const mobileSlides = section.querySelectorAll("[data-mobile-slide]");
    const mobileDots = section.querySelectorAll("[data-mobile-dot]");
    let runMobileTrackResize = () => {};

    let mobileLayoutSyncTimer = 0;
    const scheduleMobileLayoutSync = () => {
      window.clearTimeout(mobileLayoutSyncTimer);
      mobileLayoutSyncTimer = window.setTimeout(() => {
        if (!isMobileViewport()) return;
        if (isMobileLandscape()) {
          syncMobileMediaMaxHeight();
          syncMobileSectionHeightFromDom();
          return;
        }
        applyPortraitMobileDetailScale();
      }, 120);
    };

    const setMobileActive = (index, options = {}) => {
      const { syncLayout = false } = options;
      mobileSlides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
      });

      mobileDots.forEach((dot, i) => {
        const isActive = i === index;
        dot.classList.toggle("is-active", isActive);
        dot.setAttribute("aria-current", isActive ? "true" : "false");
      });

      if (syncLayout) {
        scheduleMobileLayoutSync();
      }
    };

    const getMobileNearestIndex = () => {
      if (!mobileTrack || mobileSlides.length === 0) return 0;
      const currentLeft = mobileTrack.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      mobileSlides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      return nearestIndex;
    };

    if (mobileTrack) {
      let mobileScrollRafId = 0;
      let scrollEndFallbackTimer = 0;
      let lastTouchEndedAt = 0;
      let isTouching = false;
      let gestureStartX = 0;
      let gestureStartY = 0;
      let gestureStartIndex = 0;
      let gestureIsHorizontal = false;
      const swipeThreshold = 24;
      let activeMobileIndex = -1;
      let mobileProgrammaticScrollUntil = 0;

      const clampMobileIndex = (index) => {
        if (mobileSlides.length === 0) return 0;
        return Math.max(0, Math.min(mobileSlides.length - 1, index));
      };

      const stopMobileProgrammaticScroll = () => {
        if (mobileScrollRafId) {
          window.cancelAnimationFrame(mobileScrollRafId);
          mobileScrollRafId = 0;
        }
      };

      const animateMobileScrollTo = (targetLeft, durationMs = 520) => {
        if (!mobileTrack) return;
        stopMobileProgrammaticScroll();
        const startLeft = mobileTrack.scrollLeft;
        const delta = targetLeft - startLeft;
        if (Math.abs(delta) < 1) {
          mobileTrack.scrollLeft = targetLeft;
          mobileProgrammaticScrollUntil = 0;
          scheduleMobileLayoutSync();
          return;
        }
        const startTime = performance.now();
        const step = (now) => {
          const progress = Math.min((now - startTime) / durationMs, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          mobileTrack.scrollLeft = startLeft + delta * eased;
          if (progress < 1) {
            mobileScrollRafId = window.requestAnimationFrame(step);
          } else {
            mobileScrollRafId = 0;
            mobileProgrammaticScrollUntil = 0;
            scheduleMobileLayoutSync();
          }
        };
        mobileScrollRafId = window.requestAnimationFrame(step);
      };

      const scrollToMobileIndex = (index, behavior = "smooth") => {
        const safeIndex = clampMobileIndex(index);
        if (isMobileLandscape()) {
          setMobileActive(safeIndex, { syncLayout: true });
          return;
        }
        const slide = mobileSlides[safeIndex];
        if (!slide || !mobileTrack) return;
        if (behavior === "auto") {
          stopMobileProgrammaticScroll();
          mobileTrack.scrollLeft = slide.offsetLeft;
          mobileProgrammaticScrollUntil = 0;
          scheduleMobileLayoutSync();
          return;
        }
        mobileProgrammaticScrollUntil = performance.now() + 600;
        animateMobileScrollTo(slide.offsetLeft);
      };

      const syncMobileActiveToNearest = () => {
        const liveIndex = getMobileNearestIndex();
        if (liveIndex !== activeMobileIndex) {
          activeMobileIndex = liveIndex;
          setMobileActive(liveIndex, { syncLayout: true });
        }
        return liveIndex;
      };

      const settleAfterScrollEnd = () => {
        if (isTouching || !mobileTrack) return;
        const targetIndex = syncMobileActiveToNearest();
        const targetSlide = mobileSlides[targetIndex];
        if (!targetSlide) return;
        const now = Date.now();
        if (now - lastTouchEndedAt < 180) return;
        const remainingOffset = Math.abs(mobileTrack.scrollLeft - targetSlide.offsetLeft);
        const correctionThreshold = Math.max(44, (mobileTrack.clientWidth || 0) * 0.3);
        // Only correct when clearly stuck between slides after scroll settles.
        if (remainingOffset <= correctionThreshold) return;
        mobileTrack.scrollTo({ left: targetSlide.offsetLeft, behavior: "smooth" });
      };

      const onMobileScroll = () => {
        if (isTouching) return;
        if (mobileProgrammaticScrollUntil && performance.now() < mobileProgrammaticScrollUntil) return;
        syncMobileActiveToNearest();
        window.clearTimeout(scrollEndFallbackTimer);
        scrollEndFallbackTimer = window.setTimeout(settleAfterScrollEnd, 140);
      };

      const onTouchStart = (event) => {
        if (!event.touches || event.touches.length === 0) return;
        isTouching = true;
        gestureStartX = event.touches[0].clientX;
        gestureStartY = event.touches[0].clientY;
        gestureStartIndex = getMobileNearestIndex();
        gestureIsHorizontal = false;
        activeMobileIndex = gestureStartIndex;
        window.clearTimeout(scrollEndFallbackTimer);
        stopMobileProgrammaticScroll();
      };

      const onTouchMove = (event) => {
        if (!isTouching || !event.touches || event.touches.length === 0) return;
        const deltaX = event.touches[0].clientX - gestureStartX;
        const deltaY = event.touches[0].clientY - gestureStartY;

        if (!gestureIsHorizontal && Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY) + 6) {
          gestureIsHorizontal = true;
        }

        if (!gestureIsHorizontal && Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX) + 6) {
          // User switched to vertical intent, release control to page scroll.
          isTouching = false;
          return;
        }

        if (!gestureIsHorizontal) {
          return;
        }

        if (Math.abs(deltaX) < swipeThreshold) {
          return;
        }
      };

      const settleTouchGesture = () => {
        if (!isTouching) return;
        isTouching = false;
        lastTouchEndedAt = Date.now();
        if (!gestureIsHorizontal) return;
        // iOS small-screen stability: let native scroll-snap settle after finger release.
        const targetIndex = getMobileNearestIndex();
        if (targetIndex !== activeMobileIndex) {
          activeMobileIndex = targetIndex;
          setMobileActive(targetIndex, { syncLayout: true });
        }
        window.clearTimeout(scrollEndFallbackTimer);
        scrollEndFallbackTimer = window.setTimeout(settleAfterScrollEnd, 220);
      };

      mobileDots.forEach((dot, dotIndex) => {
        dot.removeAttribute("disabled");
        dot.setAttribute("aria-disabled", "false");
        dot.setAttribute("tabindex", "0");

        const activateMobileDot = () => {
          if (!revealController.isRevealed()) {
            revealController.setRevealState(true, true);
            section.scrollIntoView({ block: "center", behavior: "smooth" });
          }
          const nextIndex = Number(dot.dataset.index || 0);
          if (nextIndex === activeMobileIndex) return;
          activeMobileIndex = nextIndex;
          setMobileActive(nextIndex);
          scrollToMobileIndex(nextIndex);
        };

        dot.addEventListener("click", activateMobileDot);
        dot.addEventListener("pointerdown", () => {
          dot.dataset.srtPointerFocus = "1";
        });
        dot.addEventListener("focus", () => {
          if (dot.dataset.srtPointerFocus) {
            delete dot.dataset.srtPointerFocus;
            return;
          }
          if (!revealController.isRevealed()) {
            revealController.setRevealState(true, true);
            section.scrollIntoView({ block: "center", behavior: "smooth" });
          }
          const nextIndex = Number(dot.dataset.index || 0);
          if (nextIndex === activeMobileIndex) return;
          activeMobileIndex = nextIndex;
          setMobileActive(nextIndex);
          scrollToMobileIndex(nextIndex);
        });
        dot.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activateMobileDot();
            return;
          }

          let nextIndex = dotIndex;
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            nextIndex = Math.min(mobileDots.length - 1, dotIndex + 1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            nextIndex = Math.max(0, dotIndex - 1);
          } else if (event.key === "Home") {
            event.preventDefault();
            nextIndex = 0;
          } else if (event.key === "End") {
            event.preventDefault();
            nextIndex = mobileDots.length - 1;
          } else {
            return;
          }

          mobileDots[nextIndex]?.focus();
        });
      });

      mobileTrack.addEventListener("scroll", onMobileScroll, { passive: true });
      mobileTrack.addEventListener("touchstart", onTouchStart, { passive: true });
      mobileTrack.addEventListener("touchmove", onTouchMove, { passive: false });
      mobileTrack.addEventListener("touchend", settleTouchGesture, { passive: true });
      mobileTrack.addEventListener("touchcancel", settleTouchGesture, { passive: true });
      if ("onscrollend" in window || "onscrollend" in mobileTrack) {
        mobileTrack.addEventListener("scrollend", settleAfterScrollEnd, { passive: true });
      }
      runMobileTrackResize = () => {
        const index = getMobileNearestIndex();
        scrollToMobileIndex(index, "auto");
        activeMobileIndex = index;
        setMobileActive(index, { syncLayout: true });
      };
      activeMobileIndex = getMobileNearestIndex();
      setMobileActive(activeMobileIndex, { syncLayout: true });
    }

    const verticalPager = createVerticalPager({
      isVerticalScrollMode,
      totalDesktopSlides,
      detailScrollScreens,
      isMobileViewportFn: isMobileViewport,
      isPadViewportFn: isPadViewport,
      activateTabFn: activateTab,
      section,
      getActiveDesktopSlide: () => activeDesktopSlide,
      setActiveDesktopSlide: (value) => {
        activeDesktopSlide = value;
      },
      getVerticalTransitionMeta: revealController.getVerticalTransitionMeta,
    });

    let ticking = false;
    let hasInitialRevealPass = false;
    const stickyPanel = section.querySelector(".scroll-reveal-tabs__sticky");
    const headerContainer = document.querySelector("[data-header-container]");
    let revealFreezeUntilMs = 0;
    let cachedViewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    let cachedStickyHeight = Math.max(
      stickyPanel ? stickyPanel.getBoundingClientRect().height : cachedViewportHeight,
      1,
    );
    let cachedScrollWindow = Math.max(section.offsetHeight - cachedViewportHeight, 1);
    let lastProgressValue = "";

    const recalcRevealMetrics = () => {
      cachedViewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      cachedStickyHeight = Math.max(stickyPanel ? stickyPanel.getBoundingClientRect().height : cachedViewportHeight, 1);
      cachedScrollWindow = Math.max(section.offsetHeight - cachedViewportHeight, 1);
    };
    section.__srtRecalcRevealMetrics = recalcRevealMetrics;

    const freezeRevealTemporarily = (durationMs = 220) => {
      revealFreezeUntilMs = Date.now() + durationMs;
    };

    const clearIntroDetailFocusTimer = () => {
      if (section.__srtIntroFocusTimer) {
        window.clearTimeout(section.__srtIntroFocusTimer);
        section.__srtIntroFocusTimer = 0;
      }
    };

    const focusWithoutScroll = (element) => {
      if (!element || typeof element.focus !== "function") return;
      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
    };

    const focusFirstDetailControlIfNeeded = () => {
      if (!section.classList.contains("is-revealed")) return;
      const active = document.activeElement;
      if (active instanceof Element && section.contains(active) && !active.matches("[data-srt-intro-region]")) {
        return;
      }
      let target = null;
      if (isMobileViewport()) {
        target = section.querySelector("[data-mobile-dot]");
      } else if (isVerticalScrollMode) {
        target = section.querySelector("[data-detail-dot]");
      } else {
        target = section.querySelector("[data-tab-btn]");
      }
      focusWithoutScroll(target);
    };

    const scheduleIntroDetailFocus = (delayMs = 0) => {
      clearIntroDetailFocusTimer();
      const run = () => {
        section.__srtIntroFocusTimer = 0;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            focusFirstDetailControlIfNeeded();
          });
        });
      };
      if (delayMs <= 0) {
        run();
        return;
      }
      section.__srtIntroFocusTimer = window.setTimeout(run, delayMs);
    };

    const revealFromIntroKeyboard = () => {
      if (section.classList.contains("is-revealed")) return;
      clearIntroDetailFocusTimer();
      recalcRevealMetrics();
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      const rect = section.getBoundingClientRect();
      const revealEnterPx = cachedViewportHeight * 0.2;
      const targetTop = scrollY + rect.top + revealEnterPx;
      window.scrollTo({ top: targetTop, behavior: "auto" });
      const useImmediateReveal = isReducedMotion();
      freezeRevealTemporarily(useImmediateReveal ? 320 : 1900);
      revealController.setRevealState(true, useImmediateReveal);
      recalcRevealMetrics();
      // Do not call updateReveal() here: scrollTo may not have updated `passed` yet,
      // which immediately reverts is-revealed (regression vs da17cd4 keyboard flow).
      syncScrollRevealTabStops(section, activeDesktopSlide);
      scheduleIntroDetailFocus(0);
    };

    const introRegion = section.querySelector("[data-srt-intro-region]");
    if (introRegion) {
      introRegion.addEventListener("keydown", (event) => {
        if (section.classList.contains("is-revealed")) return;
        if (event.key === "Tab" && !event.shiftKey) {
          event.preventDefault();
          revealFromIntroKeyboard();
          return;
        }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          revealFromIntroKeyboard();
        }
      });
    }

    const getMobileBottomInView = () => {
      if (!isMobileViewport() || !isMobileLandscape()) return true;
      const overflowPx =
        Number.parseFloat(section.style.getPropertyValue("--srt-sticky-extra-height")) || 0;
      return overflowPx <= 2;
    };

    const updateReveal = () => {
      const rect = section.getBoundingClientRect();
      const passed = Math.min(Math.max(-rect.top, 0), cachedScrollWindow);
      const progress = passed / cachedScrollWindow;
      const revealResult = revealController.syncRevealFromScroll({
        passed,
        rect,
        viewportHeight: cachedViewportHeight,
        hasInitialRevealPass,
        mobileBottomInView: getMobileBottomInView(),
      });

      verticalPager.updateFromScroll({
        shouldReveal: revealResult.shouldReveal,
        passed,
        stickyHeight: cachedStickyHeight,
        revealEnterPx: revealResult.revealEnterPx,
      });

      const progressValue = progress.toFixed(3);
      if (progressValue !== lastProgressValue) {
        section.style.setProperty("--progress", progressValue);
        lastProgressValue = progressValue;
      }
      hasInitialRevealPass = true;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        if (Date.now() < revealFreezeUntilMs) {
          ticking = false;
          return;
        }
        updateReveal();
        ticking = false;
      });
    };

    updateMobileDetailScale();
    bindMobileLayoutObserver();
    recalcRevealMetrics();
    section.style.setProperty("--scroll-screens", String(scrollScreens));
    let resizeRafId = 0;
    let resizeDebounceTimer = 0;
    let lastResizeWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    let lastResizeHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const clearResizeWork = () => {
      if (resizeRafId) {
        window.cancelAnimationFrame(resizeRafId);
        resizeRafId = 0;
      }
      if (resizeDebounceTimer) {
        window.clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = 0;
      }
    };
    const commitResizeWork = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const rect = section.getBoundingClientRect();
      const isNearViewport = rect.bottom > -viewportHeight && rect.top < viewportHeight * 2;
      if (!isNearViewport) return;
      responsiveReset.handleResize();
      runMobileTrackResize();
      updateMobileDetailScale();
      recalcRevealMetrics();
      onScroll();
    };
    const onResize = () => {
      const nextWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const nextHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const widthChanged = Math.abs(nextWidth - lastResizeWidth) >= 2;
      const heightChanged = Math.abs(nextHeight - lastResizeHeight) >= 48;
      if (!widthChanged && !heightChanged) return;
      if (isMobileViewport() && !widthChanged) {
        lastResizeHeight = nextHeight;
        onScroll();
        return;
      }
      lastResizeWidth = nextWidth;
      lastResizeHeight = nextHeight;
      if (resizeRafId) window.cancelAnimationFrame(resizeRafId);
      resizeRafId = window.requestAnimationFrame(() => {
        resizeRafId = 0;
        if (resizeDebounceTimer) window.clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = window.setTimeout(commitResizeWork, 80);
      });
    };

    let listenersBound = false;
    let sectionInViewport = true;
    let documentVisible = !document.hidden;

    const bindWindowListeners = () => {
      if (listenersBound) return;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });
      listenersBound = true;
    };

    const unbindWindowListeners = () => {
      if (!listenersBound) return;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearResizeWork();
      listenersBound = false;
    };

    const syncWindowListenerBinding = () => {
      const shouldBind = sectionInViewport && documentVisible;
      if (shouldBind) {
        bindWindowListeners();
        updateMobileDetailScale();
        recalcRevealMetrics();
        updateReveal();
      } else {
        unbindWindowListeners();
      }
    };

    if ("IntersectionObserver" in window) {
      const visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target !== section) return;
            sectionInViewport = entry.isIntersecting;
            syncWindowListenerBinding();
          });
        },
        { root: null, threshold: 0 },
      );
      visibilityObserver.observe(section);
    } else {
      sectionInViewport = true;
    }

    document.addEventListener(
      "visibilitychange",
      () => {
        documentVisible = !document.hidden;
        syncWindowListenerBinding();
      },
      { passive: true },
    );

    if (headerContainer) {
      const onHeaderTransitionStart = (event) => {
        if (event && event.propertyName && event.propertyName !== "transform") return;
        freezeRevealTemporarily(260);
      };
      const onHeaderTransitionEnd = (event) => {
        if (event && event.propertyName && event.propertyName !== "transform") return;
        freezeRevealTemporarily(80);
        updateMobileDetailScale();
        recalcRevealMetrics();
        onScroll();
      };

      headerContainer.addEventListener("transitionrun", onHeaderTransitionStart, { passive: true });
      headerContainer.addEventListener("transitionstart", onHeaderTransitionStart, { passive: true });
      headerContainer.addEventListener("transitionend", onHeaderTransitionEnd, { passive: true });
      headerContainer.addEventListener("transitioncancel", onHeaderTransitionEnd, { passive: true });
    }

    syncWindowListenerBinding();
  };

  document.querySelectorAll(SELECTOR).forEach(setupSection);

  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target.querySelector(SELECTOR) || event.target.closest(SELECTOR);
    if (section) setupSection(section);
  });
})();
